<template>
  <section ref="page" tabindex="-1" :aria-label="title">
    <AppPageContainer :custom="1600" :min-height="0">
      <AppPageHeader design="new" :title="operations ? 'Message Operations' : 'Integration Logs'" compact />
      <AppTabs design="new" refined weight="medium" :tabs="tabs" />
      <main class="ui-page">
        <OperationsFilters :query="query" :options="options" :integration="integration" :operations="operations" :search="search" :loading="optionsLoading" :active="hasFilters || Boolean(search) || Boolean(invalidQuery)" @search="queueSearch" @submit-search="submitSearch" @change="changeFilters" @clear="clearFilters">
          <template v-if="operations" #selection><AppFilters name="operation-view" design="new" refined weight="medium" :filters="viewOptions" :value="query.view || 'all_operations'" aria-label="Operation view" :aria-busy="summaryLoading ? 'true' : 'false'" @change="changeFilters({ view: $event })" /></template>
          <template #actions><AppIconButton label="Refresh" tooltip-placement="left" :disabled="loading" @click="refresh"><i class="pi pi-refresh" aria-hidden="true" /></AppIconButton></template>
        </OperationsFilters>
        <AppErrorState v-if="optionsError" :message="optionsError" icon-only retry-label="Retry filter options" @retry="loadOptions" />
        <AppErrorState v-if="summaryError" :message="summaryError" icon-only retry-label="Retry counts" @retry="loadSummary" />
        <AppContentSkeleton v-if="loading" :label="operations ? 'Loading operations' : 'Loading logs'" :columns="skeletonColumns" min-width="1200px" />
        <AppErrorState v-else-if="invalidQuery" :message="invalidQuery" icon-only retry-label="Clear filters" @retry="clearFilters" />
        <AppErrorState v-else-if="listError" :message="listError" icon-only @retry="loadList" />
        <template v-else-if="list">
          <component :is="operations ? 'OperationsTable' : 'LogsTable'" v-if="list.results.length" :rows="list.results" :integration="integration" :operations="operations" @open="openRecord" />
          <AppNoResults v-else design="new" :title="emptyTitle" text="" />
          <AppPagination design="new" placement="embedded" :page="page" :pages="list.pages" :count="list.count" :visible-count="list.results.length" :page-size="40" :label="operations ? 'Operation pages' : 'Log pages'" @change="changePage" />
        </template>
      </main>
    </AppPageContainer>
    <RecordModal
      v-if="recordId"
      :key="`${workspace}:${integration}:${recordId}`"
      :id="recordId"
      :operations="operations"
      :integration="integration"
      @close="closeRecord"
      @changed="refresh"
    />
  </section>
</template>

<script>
import AppTabs from '@/components/AppTabs'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import { getMessageOperations, getMessageOperationsSummary, getMessageOperationFilterOptions, getIntegrationLogs, getIntegrationLogFilterOptions } from '@/http'
import RecordTable from './RecordTable'
import RecordModal from './RecordModal'
import OperationsFilters from './OperationsFilters.vue'
import OperationsTable from './OperationsTable.vue'
import LogsTable from './LogsTable.vue'
import { integrations, views, humanize, cleanQuery, listParams, queryError, errorMessage, scalar, normalizePage } from './model'
import './workspace.scss'

export default {
  name: 'IntegrationWorkspace',
  components: { AppTabs, AppContentSkeleton, AppErrorState, AppIconButton, RecordModal, OperationsFilters, OperationsTable, LogsTable },
  props: { workspace: { type: String, required: true }, integration: { type: String, required: true } },
  data: () => ({
    list: null,
    summary: null,
    options: {},
    loading: false,
    summaryLoading: false,
    optionsLoading: false,
    listError: '',
    summaryError: '',
    optionsError: '',
    search: '',
    searchTimer: null,
    pollTimer: null,
    pollBusy: false,
    listGeneration: 0,
    summaryGeneration: 0,
    optionsGeneration: 0,
    trigger: null,
    navigationPending: false
  }),
  computed: {
    operations() { return this.workspace === 'message-operations' },
    scope() { return `${this.workspace}:${this.integration}` },
    query() { return this.$route.query },
    title() { return `${integrations[this.integration]} ${this.operations ? 'Operations' : 'Integration Logs'}` },
    tabs() { return Object.keys(integrations).map(id => ({ id, label: integrations[id], name: `tech-support-${this.workspace}-${id}` })) },
    params() { return listParams(this.query, this.integration, this.operations) },
    summaryParams() { return listParams(this.query, this.integration, true, true) },
    listKey() { return JSON.stringify([this.scope, this.params, this.invalidQuery]) },
    summaryKey() { return JSON.stringify([this.scope, this.summaryParams, this.invalidQuery]) },
    invalidQuery() { return queryError(this.query, this.operations) },
    page() { return Number(this.query.page || 1) },
    recordId() { return scalar(this.query[this.operations ? 'operation' : 'log']) },
    hasFilters() { return Object.keys(this.params).some(key => !['integration', 'page', 'page_size', 'view'].includes(key)) || (this.operations && this.params.view !== 'all_operations') },
    emptyTitle() { return this.hasFilters ? `No ${this.operations ? 'operations' : 'logs'} match these filters` : this.operations ? 'No tracked operations are available' : 'No saved logs are available' },
    skeletonColumns() { return this.operations ? OperationsTable.computed.columns.call(this) : RecordTable.computed.columns.call(this) },
    viewOptions() { return views.map(value => ({ value, label: `${humanize(value)} (${this.operations ? this.summary?.[value] ?? (this.summaryLoading ? '…' : '—') : this.summaryLoading ? '…' : this.summary?.[value] ?? '—'})` })) }
  },
  watch: {
    scope: { immediate: true, handler() { clearTimeout(this.searchTimer); this.search = scalar(this.query.search); if (this.operations) this.summary = null; this.loadOptions() } },
    'query.search'(value) { clearTimeout(this.searchTimer); this.search = scalar(value) },
    listKey: { immediate: true, handler() { this.loadList() } },
    summaryKey: { immediate: true, handler() { this.loadSummary() } },
    recordId(id, previous) {
      if (!id && previous) this.$nextTick(() => {
        const target = this.trigger && document.contains(this.trigger) ? this.trigger : this.$refs.page
        if (target) target.focus({ preventScroll: true })
      })
    }
  },
  mounted() {
    this.pollTimer = setInterval(this.pollKnowledge, 5000)
  },
  beforeDestroy() {
    clearInterval(this.pollTimer)
    clearTimeout(this.searchTimer)
    this.listGeneration++
    this.summaryGeneration++
    this.optionsGeneration++
  },
  methods: {
    async pollKnowledge() {
      if (this.integration !== 'knowledge_ai' || !this.operations || document.hidden || this.loading || this.pollBusy || this.invalidQuery || this.listError) return
      this.pollBusy = true
      const generation = this.listGeneration
      const summaryGeneration = this.summaryGeneration
      try {
        const [list, summary] = await Promise.all([getMessageOperations(this.params), getMessageOperationsSummary(this.summaryParams)])
        if (generation === this.listGeneration) this.list = normalizePage(list.data)
        if (summaryGeneration === this.summaryGeneration) this.summary = summary.data
      } catch (error) {
        if (generation === this.listGeneration) this.listError = errorMessage(error)
      } finally {
        this.pollBusy = false
      }
    },
    async loadOptions() {
      const generation = ++this.optionsGeneration
      this.optionsLoading = true
      this.optionsError = ''
      this.options = {}
      try {
        const response = await (this.operations ? getMessageOperationFilterOptions({ integration: this.integration }) : getIntegrationLogFilterOptions(this.integration))
        if (generation === this.optionsGeneration) this.options = response.data
      } catch (error) {
        if (generation === this.optionsGeneration) this.optionsError = errorMessage(error, 'Filter options')
      } finally {
        if (generation === this.optionsGeneration) this.optionsLoading = false
      }
    },
    async loadList() {
      const generation = ++this.listGeneration
      this.listError = ''
      this.list = null
      this.loading = !this.invalidQuery
      if (this.invalidQuery) return
      const fetchList = this.operations ? getMessageOperations : params => getIntegrationLogs(this.integration, params)
      const params = { ...this.params }
      try {
        const response = await fetchList(params)
        if (generation === this.listGeneration) this.list = normalizePage(response.data)
      } catch (error) {
        if (generation !== this.listGeneration) return
        if (error?.response?.status === 404 && Number(params.page) > 1) {
          try {
            const response = await fetchList({ ...params, page: 1 })
            if (generation !== this.listGeneration) return
            const { pages } = normalizePage(response.data)
            if (pages < Number(params.page)) {
              await this.navigate({ ...this.query, page: String(pages) }, true)
              return
            }
          } catch (pageError) {
            if (generation === this.listGeneration) this.listError = errorMessage(pageError)
          }
        }
        if (generation === this.listGeneration && !this.listError) this.listError = errorMessage(error)
      } finally {
        if (generation === this.listGeneration) this.loading = false
      }
    },
    async loadSummary() {
      const generation = ++this.summaryGeneration
      if (!this.operations) this.summary = null
      this.summaryError = ''
      this.summaryLoading = this.operations && !this.invalidQuery
      if (!this.summaryLoading) return
      try {
        const response = await getMessageOperationsSummary(this.summaryParams)
        if (generation === this.summaryGeneration) this.summary = response.data
      } catch (error) {
        if (generation === this.summaryGeneration) this.summaryError = errorMessage(error, 'Counts')
      } finally {
        if (generation === this.summaryGeneration) this.summaryLoading = false
      }
    },
    refresh() { this.loadList(); this.loadSummary() },
    navigate(query, replace = false) {
      return this.$router[replace ? 'replace' : 'push']({ path: this.$route.path, query: cleanQuery(query) }).catch(error => {
        if (error.name !== 'NavigationDuplicated') this.listError = 'Unable to update this view. Try again.'
      })
    },
    queueSearch(value) {
      this.search = value || ''
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.submitSearch, 300)
    },
    submitSearch() {
      clearTimeout(this.searchTimer)
      this.changeFilters({ search: this.search.trim() })
    },
    changeFilters(patch) {
      clearTimeout(this.searchTimer)
      this.navigate({ ...this.query, search: this.search.trim(), ...patch, page: undefined })
    },
    changePage(page) { this.navigate({ ...this.query, page: String(page) }) },
    clearFilters() { clearTimeout(this.searchTimer); this.search = ''; this.navigate({}) },
    async openRecord(id, trigger) {
      if (this.navigationPending) return
      clearTimeout(this.searchTimer)
      this.search = scalar(this.query.search)
      this.navigationPending = true
      this.trigger = trigger
      const source = this.$route.fullPath
      await this.navigate({ ...this.query, [this.operations ? 'operation' : 'log']: id })
      if (this.recordId === id) window.history.replaceState({ ...window.history.state, integrationReturn: source }, '')
      this.navigationPending = false
    },
    closeRecord() {
      const query = { ...this.query }
      delete query[this.operations ? 'operation' : 'log']
      const target = this.$router.resolve({ path: this.$route.path, query }).route.fullPath
      if (window.history.state?.integrationReturn === target) this.$router.back()
      else this.navigate(query, true)
    }
  }
}
</script>
